import { Component, inject, Input, OnInit } from '@angular/core';
import { FormGroupErrorMessageComponent, LoadingPanelService, Options, TypeChecker, Utils } from 'lib-base';
import { TablasDinamicasConfigService } from '../../../../services/database/tablas-dinamicas-config.service';
import { IColumnDinamico, TablasDinamicasConfig } from '../../../../shared/interfaces/database/tramites_automaticos';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { firstValueFrom, forkJoin } from 'rxjs';
import { ControlOptionsComponent } from './control-options.component';
import ExcelJS from 'exceljs';
import { ColumnType } from '../../../../pages/configuracion/datasets-dinamicos/dataset-dinamico-columnas';

@Component({
  selector: 'app-data-source-config-tram-automaticos',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CommonModule, DropdownModule, TranslateModule, FormGroupErrorMessageComponent, ButtonModule],
  template: `
  <form [formGroup]="form" (ngSubmit)="onSubmit()">
    <main class="grid formgrid p-fluid">
      <div class="field col-12">
        <label for="tablaDinamicaConfigId" class="ml-3">
          {{'fields.tipo-tramite-actividad.tablaDinamica' | translate}}
          <span style="color: red">*</span>
        </label>
        <div class="flex gap-1 justify-content-start">
          <p-dropdown [options]="tablasDinamicas" formControlName="tablaDinamicaConfigId" appendTo="body"
            placeholder="Select" optionLabel="datasetNombre" optionValue="id" [filter]="true"
            autoWidth="false" class="flex-grow-1" (onChange)="onChangeTablaDinamica($event)">
          </p-dropdown>
        </div>
        <ng-container
          *ngIf="form.get('tablaDinamicaConfigId')?.invalid && form.get('tablaDinamicaConfigId')?.dirty">
          <app-form-group-error-message
            [control]="form.get('tablaDinamicaConfigId')"></app-form-group-error-message>
        </ng-container>
      </div>
      <div class="field col-12">
        <label for="columnName" class="ml-3">
          {{'common.columnName' | translate}}
          <span style="color: red">*</span>
        </label>
        <div class="flex gap-1 justify-content-start">
          <p-dropdown [options]="columnsTablaDinamica" formControlName="columnName" appendTo="body"
            placeholder="Select" optionLabel="displayName" optionValue="name"
            autoWidth="false" class="flex-grow-1">
          </p-dropdown>
        </div>
        <ng-container
          *ngIf="form.get('columnName')?.invalid && form.get('columnName')?.dirty">
          <app-form-group-error-message
            [control]="form.get('columnName')"></app-form-group-error-message>
        </ng-container>
      </div>
      <div class="field col-12">
        <label for="columnValue" class="ml-3">
          {{'common.columnValue' | translate}}
          <span style="color: red">*</span>
        </label>
        <div class="flex gap-1 justify-content-start">
          <p-dropdown [options]="columnsTablaDinamica" formControlName="columnValue" appendTo="body"
            placeholder="Select" optionLabel="displayName" optionValue="name"
            autoWidth="false" class="flex-grow-1">
          </p-dropdown>
        </div>
        <ng-container
          *ngIf="form.get('columnValue')?.invalid && form.get('columnValue')?.dirty">
          <app-form-group-error-message
            [control]="form.get('columnValue')"></app-form-group-error-message>
        </ng-container>
      </div>
      <div class="field col-12">
        <button pButton type="submit" icon="pi pi-file-edit"
              [label]="'form.button.aplicar' | translate"
              class="button-edit p-button-outlined"></button>
      </div>
    </main>
  </form>
  `,
})
export class DataSourceConfigTramAutomaticosComponent implements OnInit {
  @Input() control?: Options<any>;
  tablasDinamicas !: TablasDinamicasConfig[];
  form !: FormGroup;
  columnsTablaDinamica: IColumnDinamico[] = [];
  trans: any = {};

  private parent: ControlOptionsComponent = inject(ControlOptionsComponent);

  constructor(private _tdc: TablasDinamicasConfigService, private readonly _fb: FormBuilder, private readonly _ms: MessageService,
    private readonly _t: TranslateService, private readonly _lps: LoadingPanelService
  ) {
    this.formConfig();
  }

  ngOnInit(): void {
    this.translate();
    this.getTablasDinamicas();
  }

  private async translate(): Promise<void> {
    this.trans = await firstValueFrom(forkJoin({
      completeCamposRequeridos: this._t.get('msg.completeCamposRequeridos'),
      advertencia: this._t.get('common.advertencia'),
    }));
  }

  private formConfig() {
    this.form = this._fb.group({
      tablaDinamicaConfigId: [null, [Validators.required]],
      columnName: [null, [Validators.required]],
      columnValue: [null, [Validators.required]],
    });
  }

  onChangeTablaDinamica(event: DropdownChangeEvent) {
    const columns: IColumnDinamico[] = this.tablasDinamicas.find(t => t.id === event.value)?.datasetColumnas ?? [];
    this.columnsTablaDinamica = columns;
    this.form.get('columnName')?.setValue(null);
    this.form.get('columnValue')?.setValue(null);
  }

  private setValue(){
    if(this.control && TypeChecker.isSourceDatasetDinamico(this.control) && this.control.tablaConsulta){
      const tablaConsultaId = this.control.tablaConsulta;          
      if(tablaConsultaId){
        this.form.get('tablaDinamicaConfigId')?.setValue(tablaConsultaId);
        const columns: IColumnDinamico[] = this.tablasDinamicas.find(t => t.id === tablaConsultaId)?.datasetColumnas ?? [];
        this.columnsTablaDinamica = columns;
        this.form.get('columnName')?.setValue(this.control.columnName);
        this.form.get('columnValue')?.setValue(this.control.columnValue);
      }
    }
  }

  public getTablasDinamicas() {
    if(this.tablasDinamicas){
      this.setValue();
      return;
    }
    this._tdc.getTablasDinamicas().subscribe({
      next: (data: TablasDinamicasConfig[]) => {
        data.forEach(td => {
          td.datasetColumnas = [
            { name: 'id', type: ColumnType.String, dataType: ExcelJS.ValueType.Number, format: '', displayName: 'ID', editable: false },
            ...td.datasetColumnas
          ];
        });
        this.tablasDinamicas = data;
        this.setValue();
      }
    });
  }

  onSubmit() {
    if (this.form.invalid) {
      Utils.markAllFormControlsAsTouched(this.form);
      this._ms.add({ severity: 'warn', summary: this.trans.advertencia, detail: this.trans.completeCamposRequeridos });
      return;
    }
    this.getFormularioDataset();
  }

  getFormularioDataset() {
    this._lps.show(true);
    this._tdc.getFormularioDataset(this.form.value.tablaDinamicaConfigId, this.form.value.columnName, this.form.value.columnValue).subscribe({
      next: (data: Record<string, any>[]) => {
        if(this.control){
          this.control.options = [];
        }
        data.forEach((item: Record<string, any>) => {
          this.control?.options.push({
            value: item[this.form.value.columnValue],
            text: item[this.form.value.columnName],
            ...item
          });
        });
        this.parent.applyDataSourceConfigTramAutomaticos(this.form.value.tablaDinamicaConfigId, this.form.value.columnName, this.form.value.columnValue);
        this._lps.hide();
      }
    });
  }
}
